import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../api.js';
import { applyBranding, getDefaultBranding, loadBrandingFromStorage } from '../utils/branding.js';
import { useAuthStore } from '../store/auth.js';
import { useI18nStore } from '../store/i18n.js';
import { EN_MESSAGES } from '../i18n/messages/en.js';
import { mergeDeep } from '../i18n/mergeMessages.js';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';
import {
  Palette,
  Globe,
  Bot,
  Upload,
  Eye,
  RotateCcw,
  Save,
  Trash2,
  Star,
  Plus,
  AlertTriangle,
  RefreshCw,
  X,
  ChevronRight,
  Image,
  FileText,
  Key,
  Cpu,
  Link2,
  CreditCard,
} from 'lucide-react';

function ColorField({ label, value, onChange }) {
  return (
    <div className="form-group" style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', marginBottom: 8 }}>{label}</label>
      <div style={{ display: 'grid', gridTemplateColumns: '56px 1fr 96px', gap: 10, alignItems: 'center' }}>
        <input type="color" value={value} onChange={onChange} style={{ width: 56, height: 38, padding: 2, borderRadius: 8 }} />
        <div style={{ height: 36, borderRadius: 8, border: '1px solid var(--border)', background: value }} />
        <input value={value} readOnly style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)', textAlign: 'center' }} />
      </div>
    </div>
  );
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
    reader.readAsDataURL(file);
  });
}

export default function AdminSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const setBundle = useI18nStore((s) => s.setBundle);
  const locale = useI18nStore((s) => s.locale);
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const [form, setForm] = useState(getDefaultBranding());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [offlineMode, setOfflineMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [editingCode, setEditingCode] = useState('en');
  const [languageName, setLanguageName] = useState('English');
  const [messagesText, setMessagesText] = useState(JSON.stringify(EN_MESSAGES, null, 2));
  const [langLoading, setLangLoading] = useState(false);
  const [langSaving, setLangSaving] = useState(false);
  const [newLanguage, setNewLanguage] = useState({ code: '', name: '' });
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  // AI Settings state
  const [aiForm, setAiForm] = useState({
    aiProvider: null,
    aiApiKey: '',
    aiModel: '',
    aiBaseUrl: '',
    aiMaxTokens: 4096,
    aiTemperature: 0.7,
  });
  const [aiProviders, setAiProviders] = useState([]);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiLoaded, setAiLoaded] = useState(false);
  const [stripeForm, setStripeForm] = useState({
    stripeMode: 'test',
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    stripeSuccessUrl: '',
    stripeCancelUrl: '',
  });
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');

  if (String(user?.role || '').toLowerCase() !== 'admin') {
    return <Navigate to="/templates" replace />;
  }

  const loadSettings = async () => {
    setLoading(true);
    setLoadError('');
    setOfflineMode(false);
    try {
      const data = await api.getAdminSettings();
      setForm({ ...getDefaultBranding(), ...data });
    } catch (err) {
      const fallback = { ...getDefaultBranding(), ...loadBrandingFromStorage() };
      setForm(fallback);
      setLoadError(err.message || t('admin.loadFailed', 'Could not load Admin Settings.'));
      setOfflineMode(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadLanguages();
    loadAISettings();
    loadStripeSettings();
  }, []);

  async function loadStripeSettings() {
    try {
      const data = await api.getBillingSettings();
      setStripeForm({
        stripeMode: data.stripeMode || 'test',
        stripePublishableKey: data.stripePublishableKey || '',
        stripeSecretKey: data.stripeSecretKey || '',
        stripeWebhookSecret: data.stripeWebhookSecret || '',
        stripeSuccessUrl: data.stripeSuccessUrl || '',
        stripeCancelUrl: data.stripeCancelUrl || '',
      });
      setStripeLoaded(true);
    } catch {
      setStripeLoaded(true);
    }
  }

  const handleSaveStripe = async () => {
    setStripeSaving(true);
    try {
      const saved = await api.updateBillingSettings({
        stripeMode: stripeForm.stripeMode,
        stripePublishableKey: stripeForm.stripePublishableKey || null,
        stripeSecretKey: stripeForm.stripeSecretKey || null,
        stripeWebhookSecret: stripeForm.stripeWebhookSecret || null,
        stripeSuccessUrl: stripeForm.stripeSuccessUrl || null,
        stripeCancelUrl: stripeForm.stripeCancelUrl || null,
      });
      setStripeForm((prev) => ({
        ...prev,
        stripeMode: saved.stripeMode || 'test',
        stripePublishableKey: saved.stripePublishableKey || '',
        stripeSecretKey: saved.stripeSecretKey || '',
        stripeWebhookSecret: saved.stripeWebhookSecret || '',
        stripeSuccessUrl: saved.stripeSuccessUrl || '',
        stripeCancelUrl: saved.stripeCancelUrl || '',
      }));
      showAlert(t('admin.stripeSaved', 'Stripe settings saved.'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
    } catch (err) {
      showAlert(err.message || t('admin.stripeSaveFailed', 'Could not save Stripe settings.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setStripeSaving(false);
    }
  };

  async function loadAISettings() {
    try {
      const data = await api.getAISettings();
      setAiForm({
        aiProvider: data.aiProvider || null,
        aiApiKey: data.aiApiKey || '',
        aiModel: data.aiModel || '',
        aiBaseUrl: data.aiBaseUrl || '',
        aiMaxTokens: data.aiMaxTokens || 4096,
        aiTemperature: data.aiTemperature ?? 0.7,
      });
      setAiProviders(data.providers || []);
      setAiLoaded(true);
    } catch {
      setAiLoaded(true);
    }
  }

  const handleSaveAI = async () => {
    setAiSaving(true);
    try {
      const payload = {
        aiProvider: aiForm.aiProvider || null,
        aiModel: aiForm.aiModel || null,
        aiBaseUrl: aiForm.aiBaseUrl || null,
        aiMaxTokens: aiForm.aiMaxTokens,
        aiTemperature: aiForm.aiTemperature,
      };
      // Only send API key if it was changed (not the masked placeholder)
      if (aiForm.aiApiKey && aiForm.aiApiKey !== '••••••••') {
        payload.aiApiKey = aiForm.aiApiKey;
      }
      const saved = await api.updateAISettings(payload);
      setAiForm({
        aiProvider: saved.aiProvider || null,
        aiApiKey: saved.aiApiKey || '',
        aiModel: saved.aiModel || '',
        aiBaseUrl: saved.aiBaseUrl || '',
        aiMaxTokens: saved.aiMaxTokens || 4096,
        aiTemperature: saved.aiTemperature ?? 0.7,
      });
      showAlert(t('admin.aiSaved', 'AI settings saved.'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
    } catch (err) {
      showAlert(err.message || t('admin.aiSaveFailed', 'Could not save AI settings.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setAiSaving(false);
    }
  };

  async function loadLanguages() {
    setLangLoading(true);
    try {
      const res = await api.getAdminLanguages();
      const items = res?.items || [];
      setLanguages(items);
      if (items.length) {
        const defaultLang = items.find((l) => l.is_default) || items.find((l) => l.code === locale) || items[0];
        await selectLanguage(defaultLang.code);
      }
    } catch {
      setLanguages([{ code: 'en', name: 'English', is_default: true }]);
      setEditingCode('en');
      setLanguageName('English');
      setMessagesText(JSON.stringify(EN_MESSAGES, null, 2));
    } finally {
      setLangLoading(false);
    }
  }

  const selectLanguage = async (code) => {
    const selected = code || 'en';
    if (selected === 'en') {
      setEditingCode('en');
      setLanguageName('English');
      setMessagesText(JSON.stringify(EN_MESSAGES, null, 2));
      return;
    }
    const detail = await api.getAdminLanguage(selected);
    const merged = mergeDeep(EN_MESSAGES, detail.messages || {});
    setEditingCode(detail.code);
    setLanguageName(detail.name || detail.code);
    setMessagesText(JSON.stringify(merged, null, 2));
  };

  const handleCreateLanguage = async () => {
    const code = newLanguage.code.trim();
    const name = newLanguage.name.trim();
    if (!code || !name) {
      showAlert(t('admin.requiredCodeName', 'Code and Name are required.'), { tone: 'warning', title: t('billing.settingsTitle', 'Validation') });
      return;
    }

    let parsedMessages = {};
    try {
      parsedMessages = JSON.parse(messagesText || '{}');
    } catch {
      showAlert(t('admin.invalidLanguageJson', 'Language JSON must be valid.'), { tone: 'warning', title: t('billing.settingsTitle', 'Validation') });
      return;
    }

    const mergedMessages = mergeDeep(EN_MESSAGES, parsedMessages);

    try {
      await api.createAdminLanguage({
        code,
        name,
        cloneFromCode: 'en',
        baseMessages: mergedMessages,
      });
      setNewLanguage({ code: '', name: '' });
      await loadLanguages();
      await selectLanguage(code);
    } catch (err) {
      showAlert(err.message || t('admin.createLanguageFailed', 'Could not create language.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleSaveLanguage = async () => {
    setLangSaving(true);
    try {
      const parsed = JSON.parse(messagesText);
      const merged = mergeDeep(EN_MESSAGES, parsed);

      if (editingCode === 'en') {
        const exists = languages.some((l) => l.code === 'en' && l.id !== 'builtin-en');
        if (!exists) {
          await api.createAdminLanguage({
            code: 'en',
            name: languageName || 'English',
            cloneFromCode: 'en',
            baseMessages: merged,
          });
        } else {
          await api.updateAdminLanguage('en', { name: languageName || 'English', messages: merged });
        }
      } else {
        await api.updateAdminLanguage(editingCode, { name: languageName, messages: merged });
      }

      const current = await api.getCurrentLanguage();
      setBundle({ locale: current.activeCode || 'en', messages: mergeDeep(EN_MESSAGES, current.messages || {}), available: current.available || [] });
      await loadLanguages();
      showAlert(t('admin.languageSaved', 'Language saved.'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
    } catch (err) {
      showAlert(err.message || t('admin.languageSaveFailed', 'Could not save language.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setLangSaving(false);
    }
  };

  const handleSetDefaultLanguage = async () => {
    try {
      if (editingCode === 'en') {
        const exists = languages.some((l) => l.code === 'en' && l.id !== 'builtin-en');
        if (!exists) {
          await api.createAdminLanguage({ code: 'en', name: languageName || 'English', cloneFromCode: 'en', baseMessages: EN_MESSAGES });
        }
      }
      await api.updateAdminLanguage(editingCode, { isDefault: true });
      const current = await api.getCurrentLanguage();
      setBundle({ locale: current.activeCode || 'en', messages: mergeDeep(EN_MESSAGES, current.messages || {}), available: current.available || [] });
      await loadLanguages();
      showAlert(t('admin.defaultUpdated', 'Default language updated.'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
    } catch (err) {
      showAlert(err.message || t('admin.defaultUpdateFailed', 'Could not update default language.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const handleDeleteLanguage = async () => {
    if (editingCode === 'en') {
      showAlert(t('admin.cannotDeleteEn', 'Cannot delete base language en.'), { tone: 'warning', title: t('billing.settingsTitle', 'Validation') });
      return;
    }
    const accepted = await showConfirm(t('admin.deleteLanguageConfirm', 'Delete language {{code}}?', { code: editingCode }), {
      title: t('admin.languageDelete', 'Delete language'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('billing.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    try {
      await api.deleteAdminLanguage(editingCode);
      await loadLanguages();
    } catch (err) {
      showAlert(err.message || t('admin.deleteLanguageFailed', 'Could not delete language.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    const fileName = String(file.name || '').toLowerCase();
    const isSvgByName = fileName.endsWith('.svg');
    const isImageMime = String(file.type || '').startsWith('image/');
    if (!isImageMime && !isSvgByName) {
      showAlert(t('admin.onlyImages', 'Only image files are allowed (including SVG).'), { tone: 'warning', title: t('billing.settingsTitle', 'Validation') });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showAlert(t('admin.fileTooLarge', 'Image must be 2MB or smaller.'), { tone: 'warning', title: t('billing.settingsTitle', 'Validation') });
      return;
    }

    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      if (type === 'logo') update('logoIconUrl', dataUrl);
      if (type === 'favicon') update('faviconUrl', dataUrl);
    } catch (err) {
      const message = err.message === 'FILE_READ_ERROR'
        ? t('admin.fileReadError', 'Could not read the selected file.')
        : (err.message || t('admin.fileReadError', 'Could not read the selected file.'));
      showAlert(message, { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await api.updateAdminSettings({
        appName: form.appName,
        logoText: form.logoText,
        logoIconUrl: form.logoIconUrl || null,
        faviconUrl: form.faviconUrl || null,
        termsOfService: form.termsOfService || '',
        privacyPolicy: form.privacyPolicy || '',
        legalNotice: form.legalNotice || '',
        accentColor: form.accentColor,
        bgPrimary: form.bgPrimary,
        bgSecondary: form.bgSecondary,
        bgSurface: form.bgSurface,
        textPrimary: form.textPrimary,
        textSecondary: form.textSecondary,
        borderColor: form.borderColor,
      });
      setForm(saved);
      applyBranding(saved);
      setOfflineMode(false);
      setLoadError('');
      showAlert(t('admin.saved', 'Admin settings saved.'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
    } catch (err) {
      applyBranding(form);
      setOfflineMode(true);
      setLoadError(err.message || t('admin.saveFailedLocal', 'Could not save to server. Applied in local mode.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = getDefaultBranding();
    setForm((prev) => ({
      ...defaults,
      termsOfService: prev.termsOfService || '',
      privacyPolicy: prev.privacyPolicy || '',
      legalNotice: prev.legalNotice || '',
    }));
    applyBranding(defaults);
  };

  const tabs = [
    { id: 'branding', label: t('admin.brandingTitle', 'Branding'), icon: Palette },
    { id: 'language', label: t('admin.languageList', 'Languages'), icon: Globe },
    { id: 'ai', label: t('admin.aiTabTitle', 'AI Config'), icon: Bot },
    { id: 'stripe', label: t('admin.stripeTabTitle', 'Stripe'), icon: CreditCard },
  ];

  if (loading) {
    return (
      <div className="page-content"><p>{t('admin.loading', 'Loading...')}</p></div>
    );
  }

  return (
    <div>
      <div className="card" style={{ borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none', marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: 13 }}>
          <span>{t('admin.breadcrumbAdmin', 'Admin')}</span>
          <ChevronRight size={14} />
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{t('admin.breadcrumbSettings', 'Settings')}</span>
          {offlineMode && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, border: '1px solid var(--warning)', color: 'var(--warning)', fontSize: 12 }}>
              <AlertTriangle size={13} />
              {t('admin.offlineModeLabel', 'Offline mode')}
            </span>
          )}
        </div>
      </div>

      <div className="card" style={{ borderRadius: 0, borderLeft: 'none', borderRight: 'none', marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: active ? '1px solid var(--accent)' : '1px solid transparent',
                  background: active ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'transparent',
                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                <Icon size={14} /> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="page-content">
        {loadError && offlineMode && (
          <div className="card" style={{ marginBottom: 12, borderColor: 'var(--warning)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <p style={{ color: 'var(--warning)', fontSize: 13 }}>
                {t('admin.localMode', 'Local mode active.')} {loadError || t('admin.localModeFallback', 'API is not reachable right now.')}
              </p>
              <button className="btn-secondary" onClick={loadSettings}>{t('admin.retry', 'Retry')}</button>
            </div>
          </div>
        )}

        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: activeTab === 'branding' ? '1fr 300px' : '1fr', gap: 20, alignItems: 'start' }}>
            <div className="hide-scrollbar" style={{ maxHeight: 'calc(100vh - 190px)', overflowY: 'auto', paddingRight: 4 }}>
            {activeTab === 'branding' && (
              <form onSubmit={handleSave} style={{ display: 'grid', gap: 12 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 6 }}>{t('admin.identityTitle', 'Identity')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{t('admin.identitySubtitle', 'App name and logo configuration')}</p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.appName', 'App name')}</label>
                      <input value={form.appName} onChange={(e) => update('appName', e.target.value)} required maxLength={80} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.logoText', 'Logo text')}</label>
                      <input value={form.logoText} onChange={(e) => update('logoText', e.target.value)} required maxLength={80} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.logoIconUrl', 'Logo icon URL')}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          {form.logoIconUrl ? <img src={form.logoIconUrl} alt={t('admin.logoAlt', 'logo')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Image size={14} style={{ color: 'var(--text-secondary)' }} />}
                        </div>
                        <input value={form.logoIconUrl || ''} onChange={(e) => update('logoIconUrl', e.target.value)} placeholder={t('admin.logoUrlPlaceholder', 'https://.../logo.png')} style={{ flex: 1 }} />
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <input ref={logoInputRef} type="file" accept="image/*,.svg,image/svg+xml" hidden onChange={(e) => handleUpload('logo', e.target.files?.[0])} />
                        <button type="button" className="btn-secondary" onClick={() => logoInputRef.current?.click()} disabled={uploading}><Upload size={14} /> {t('admin.upload', 'Upload')}</button>
                        <button type="button" className="btn-secondary" onClick={() => update('logoIconUrl', null)}><X size={14} /> {t('admin.clear', 'Clear')}</button>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.faviconUrl', 'Favicon URL')}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          {form.faviconUrl ? <img src={form.faviconUrl} alt={t('admin.faviconAlt', 'favicon')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Image size={14} style={{ color: 'var(--text-secondary)' }} />}
                        </div>
                        <input value={form.faviconUrl || ''} onChange={(e) => update('faviconUrl', e.target.value)} placeholder={t('admin.faviconUrlPlaceholder', 'https://.../favicon.ico')} style={{ flex: 1 }} />
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <input ref={faviconInputRef} type="file" accept="image/*,.svg,image/svg+xml" hidden onChange={(e) => handleUpload('favicon', e.target.files?.[0])} />
                        <button type="button" className="btn-secondary" onClick={() => faviconInputRef.current?.click()} disabled={uploading}><Upload size={14} /> {t('admin.upload', 'Upload')}</button>
                        <button type="button" className="btn-secondary" onClick={() => update('faviconUrl', null)}><X size={14} /> {t('admin.clear', 'Clear')}</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><Palette size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.colorPaletteTitle', 'Color Palette')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{t('admin.colorPaletteSubtitle', 'Customize the visual identity of your application')}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <ColorField label={t('admin.colors.accent', 'Accent')} value={form.accentColor} onChange={(e) => update('accentColor', e.target.value)} />
                    <ColorField label={t('admin.colors.bgPrimary', 'Background Primary')} value={form.bgPrimary} onChange={(e) => update('bgPrimary', e.target.value)} />
                    <ColorField label={t('admin.colors.bgSecondary', 'Background Secondary')} value={form.bgSecondary} onChange={(e) => update('bgSecondary', e.target.value)} />
                    <ColorField label={t('admin.colors.bgSurface', 'Background Surface')} value={form.bgSurface} onChange={(e) => update('bgSurface', e.target.value)} />
                    <ColorField label={t('admin.colors.textPrimary', 'Text Primary')} value={form.textPrimary} onChange={(e) => update('textPrimary', e.target.value)} />
                    <ColorField label={t('admin.colors.textSecondary', 'Text Secondary')} value={form.textSecondary} onChange={(e) => update('textSecondary', e.target.value)} />
                    <ColorField label={t('admin.colors.border', 'Border')} value={form.borderColor} onChange={(e) => update('borderColor', e.target.value)} />
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><FileText size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.legalContentTitle', 'Legal content')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{t('admin.legalContentSubtitle', 'Edit the public legal pages shown in the landing footer.')}</p>

                  <div className="form-group">
                    <label>{t('admin.termsOfService', 'Terms of Service')}</label>
                    <textarea rows={8} value={form.termsOfService || ''} onChange={(e) => update('termsOfService', e.target.value)} style={{ width: '100%' }} />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.privacyPolicy', 'Privacy Policy')}</label>
                    <textarea rows={8} value={form.privacyPolicy || ''} onChange={(e) => update('privacyPolicy', e.target.value)} style={{ width: '100%' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('admin.legalNotice', 'Legal Notice')}</label>
                    <textarea rows={6} value={form.legalNotice || ''} onChange={(e) => update('legalNotice', e.target.value)} style={{ width: '100%' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn-secondary" onClick={handleReset}><RotateCcw size={14} /> {t('admin.resetPreview', 'Reset preview')}</button>
                    <button type="button" className="btn-secondary" onClick={() => applyBranding(form)}><Eye size={14} /> {t('admin.applyPreview', 'Apply preview')}</button>
                  </div>
                  <button type="submit" className="btn-primary" disabled={saving || uploading}>
                    {saving ? <><RefreshCw size={14} className="spin" /> {t('admin.saving', 'Saving...')}</> : <><Save size={14} /> {t('admin.save', 'Save changes')}</>}
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'language' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><Globe size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.selectLanguageTitle', 'Select Language')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{t('admin.selectLanguageSubtitle', 'Choose a language to edit translations')}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.languageList', 'Languages')}</label>
                      <select value={editingCode} onChange={(e) => selectLanguage(e.target.value)} disabled={langLoading}>
                        {languages.map((lang) => (
                          <option key={lang.code} value={lang.code}>{lang.name} ({lang.code}){lang.is_default ? ` • ${t('admin.defaultBadge', 'default')}` : ''}</option>
                        ))}
                        {!languages.some((l) => l.code === 'en') && <option value="en">{`English (en) • ${t('admin.defaultBadge', 'default')}`}</option>}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.languageName', 'Name')}</label>
                      <input value={languageName} onChange={(e) => setLanguageName(e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {editingCode !== 'en' && <button type="button" className="btn-secondary" onClick={handleSetDefaultLanguage}><Star size={14} /> {t('admin.languageSetDefault', 'Set as default')}</button>}
                    {editingCode !== 'en' && <button type="button" className="btn-secondary" onClick={handleDeleteLanguage}><Trash2 size={14} /> {t('admin.languageDelete', 'Delete language')}</button>}
                    <div style={{ marginLeft: 'auto' }}>
                      <button type="button" className="btn-primary" onClick={handleSaveLanguage} disabled={langSaving}>{langSaving ? <><RefreshCw size={14} className="spin" /> {t('admin.saving', 'Saving...')}</> : <><Save size={14} /> {t('admin.languageSave', 'Save language')}</>}</button>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><Plus size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.createLanguageTitle', 'Create New Language')}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.languageCode', 'Code')}</label>
                      <input value={newLanguage.code} onChange={(e) => setNewLanguage((prev) => ({ ...prev, code: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.languageName', 'Name')}</label>
                      <input value={newLanguage.name} onChange={(e) => setNewLanguage((prev) => ({ ...prev, name: e.target.value }))} />
                    </div>
                    <button type="button" className="btn-primary" onClick={handleCreateLanguage}><Plus size={14} /> {t('templates.modal.create', 'Create')}</button>
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 10 }}><FileText size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.languageEditor', 'Messages JSON')}</h3>
                  <textarea rows={20} value={messagesText} onChange={(e) => setMessagesText(e.target.value)} style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                </div>
              </div>
            )}

            {activeTab === 'ai' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><Bot size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.aiProviderModelTitle', 'Provider & Model')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{t('admin.aiSubtitle', 'Configure the AI provider for template and script generation.')}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.aiProvider', 'Provider')}</label>
                      <select
                        value={aiForm.aiProvider || ''}
                        onChange={(e) => {
                          const prov = e.target.value || null;
                          const def = aiProviders.find((p) => p.id === prov);
                          setAiForm((prev) => ({ ...prev, aiProvider: prov, aiModel: def?.defaultModel || prev.aiModel, aiBaseUrl: '' }));
                        }}
                        disabled={!aiLoaded}
                      >
                        <option value="">{t('admin.aiNoneOption', '— None —')}</option>
                        {aiProviders.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.aiModel', 'Model')}</label>
                      <input value={aiForm.aiModel || ''} onChange={(e) => setAiForm((prev) => ({ ...prev, aiModel: e.target.value }))} placeholder={t('admin.aiModelPlaceholder', 'e.g. gpt-4o')} />
                    </div>
                  </div>
                  {aiForm.aiProvider && (
                    <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <Cpu size={13} /> {t('admin.aiDefaultModelFor', 'Default model for {{provider}}: {{model}}', { provider: aiProviders.find((p) => p.id === aiForm.aiProvider)?.name || aiForm.aiProvider, model: aiProviders.find((p) => p.id === aiForm.aiProvider)?.defaultModel || t('admin.aiCustomModel', 'custom') })}
                    </div>
                  )}
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><Key size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.aiAuthTitle', 'Authentication')}</h3>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('admin.aiApiKey', 'API Key')}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <Key size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                        <input type="password" value={aiForm.aiApiKey || ''} onChange={(e) => setAiForm((prev) => ({ ...prev, aiApiKey: e.target.value }))} placeholder={t('admin.aiApiKeyPlaceholder', 'sk-...')} style={{ paddingLeft: 30 }} />
                      </div>
                      <button type="button" className="btn-secondary" onClick={() => setAiForm((prev) => ({ ...prev, aiApiKey: '' }))}><X size={14} /> {t('admin.clear', 'Clear')}</button>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6, display: 'block' }}>
                      {aiForm.aiApiKey === '••••••••'
                        ? t('admin.aiApiKeySet', 'Key is set')
                        : aiForm.aiApiKey
                          ? t('admin.aiNewKeyEntered', 'New key entered — save to apply')
                          : t('admin.aiApiKeyNotSet', 'No key configured')}
                    </span>
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: 6 }}>{t('admin.aiAdvancedTitle', 'Advanced Configuration')}</h3>
                  <div className="form-group">
                    <label>{t('admin.aiBaseUrl', 'Custom Base URL')}</label>
                    <div style={{ position: 'relative' }}>
                      <Link2 size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                      <input value={aiForm.aiBaseUrl || ''} onChange={(e) => setAiForm((prev) => ({ ...prev, aiBaseUrl: e.target.value }))} placeholder={t('admin.aiBaseUrlPlaceholder', 'Leave empty for default')} style={{ paddingLeft: 30 }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.aiMaxTokens', 'Max Tokens')}</label>
                      <input type="number" min={256} max={32768} value={aiForm.aiMaxTokens} onChange={(e) => setAiForm((prev) => ({ ...prev, aiMaxTokens: parseInt(e.target.value) || 4096 }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.aiTemperature', 'Temperature')}</label>
                      <input type="number" min={0} max={2} step={0.1} value={aiForm.aiTemperature} onChange={(e) => setAiForm((prev) => ({ ...prev, aiTemperature: parseFloat(e.target.value) || 0.7 }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button type="button" className="btn-primary" onClick={handleSaveAI} disabled={aiSaving}>{aiSaving ? <><RefreshCw size={14} className="spin" /> {t('admin.saving', 'Saving...')}</> : <><Save size={14} /> {t('admin.aiSaveSettings', 'Save AI Settings')}</>}</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stripe' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div className="card">
                  <h3 style={{ marginBottom: 6 }}><CreditCard size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('admin.stripeConfigTitle', 'Stripe Configuration')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{t('admin.stripeConfigSubtitle', 'Configure Stripe credentials for plan payments in MXN.')}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.stripeMode', 'Mode')}</label>
                      <select
                        value={stripeForm.stripeMode}
                        onChange={(e) => setStripeForm((prev) => ({ ...prev, stripeMode: e.target.value || 'test' }))}
                        disabled={!stripeLoaded}
                      >
                        <option value="test">Test</option>
                        <option value="live">Live</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.stripePublishableKey', 'Publishable Key')}</label>
                      <input
                        value={stripeForm.stripePublishableKey || ''}
                        onChange={(e) => setStripeForm((prev) => ({ ...prev, stripePublishableKey: e.target.value }))}
                        placeholder="pk_test_..."
                        disabled={!stripeLoaded}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.stripeSecretKey', 'Secret Key')}</label>
                      <input
                        type="password"
                        value={stripeForm.stripeSecretKey || ''}
                        onChange={(e) => setStripeForm((prev) => ({ ...prev, stripeSecretKey: e.target.value }))}
                        placeholder="sk_test_..."
                        disabled={!stripeLoaded}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.stripeWebhookSecret', 'Webhook Secret')}</label>
                      <input
                        type="password"
                        value={stripeForm.stripeWebhookSecret || ''}
                        onChange={(e) => setStripeForm((prev) => ({ ...prev, stripeWebhookSecret: e.target.value }))}
                        placeholder="whsec_..."
                        disabled={!stripeLoaded}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.stripeSuccessUrl', 'Success URL')}</label>
                      <input
                        value={stripeForm.stripeSuccessUrl || ''}
                        onChange={(e) => setStripeForm((prev) => ({ ...prev, stripeSuccessUrl: e.target.value }))}
                        placeholder="https://app.midominio.com/templates?billing=success"
                        disabled={!stripeLoaded}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.stripeCancelUrl', 'Cancel URL')}</label>
                      <input
                        value={stripeForm.stripeCancelUrl || ''}
                        onChange={(e) => setStripeForm((prev) => ({ ...prev, stripeCancelUrl: e.target.value }))}
                        placeholder="https://app.midominio.com/register?billing=cancel"
                        disabled={!stripeLoaded}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button type="button" className="btn-primary" onClick={handleSaveStripe} disabled={!stripeLoaded || stripeSaving}>
                      {stripeSaving ? <><RefreshCw size={14} className="spin" /> {t('admin.saving', 'Saving...')}</> : <><Save size={14} /> {t('admin.save', 'Save changes')}</>}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {activeTab === 'branding' && <div className="card" style={{ position: 'sticky', top: 20 }}>
            <h3 style={{ marginBottom: 12, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}><Eye size={14} /> {t('admin.previewTitle', 'Preview')}</h3>
            <div style={{ border: `1px solid ${form.borderColor}`, borderRadius: 10, overflow: 'hidden', background: form.bgPrimary }}>
              <div style={{ background: form.bgSecondary, borderBottom: `1px solid ${form.borderColor}`, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                {form.logoIconUrl ? <img src={form.logoIconUrl} alt={t('admin.logoAlt', 'logo')} style={{ width: 18, height: 18, borderRadius: 4 }} /> : <div style={{ width: 18, height: 18, borderRadius: 4, background: form.accentColor }} />}
                <span style={{ color: form.textPrimary, fontWeight: 600, fontSize: 13 }}>{form.logoText || form.appName}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <div style={{ width: 28, height: 6, borderRadius: 999, background: form.textSecondary, opacity: 0.3 }} />
                  <div style={{ width: 28, height: 6, borderRadius: 999, background: form.textSecondary, opacity: 0.3 }} />
                  <div style={{ width: 28, height: 6, borderRadius: 999, background: form.textSecondary, opacity: 0.3 }} />
                </div>
              </div>
              <div style={{ padding: 12, background: form.bgPrimary }}>
                <div style={{ width: 88, height: 6, borderRadius: 999, background: form.textPrimary, opacity: 0.75, marginBottom: 8 }} />
                <div style={{ width: 130, height: 5, borderRadius: 999, background: form.textSecondary, opacity: 0.45, marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <button type="button" style={{ background: form.accentColor, color: form.bgPrimary, border: 'none', padding: '6px 10px', borderRadius: 8, fontWeight: 600, fontSize: 12 }}>{t('admin.primaryButton', 'Primary Button')}</button>
                  <button type="button" style={{ background: 'transparent', color: form.accentColor, border: `1px solid ${form.accentColor}`, padding: '6px 10px', borderRadius: 8, fontWeight: 500, fontSize: 12 }}>{t('admin.outlineButton', 'Outline')}</button>
                </div>
                <div style={{ border: `1px solid ${form.borderColor}`, borderRadius: 8, background: form.bgSurface, padding: 10 }}>
                  <div style={{ width: 90, height: 6, borderRadius: 999, background: form.textPrimary, opacity: 0.5, marginBottom: 8 }} />
                  <div style={{ width: 130, height: 4, borderRadius: 999, background: form.textSecondary, opacity: 0.35 }} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('admin.paletteTitle', 'Current palette')}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { label: t('admin.colors.accent', 'Accent'), value: form.accentColor },
                  { label: t('admin.paletteBg', 'BG'), value: form.bgPrimary },
                  { label: t('admin.paletteSurface', 'Surface'), value: form.bgSurface },
                  { label: t('admin.paletteText', 'Text'), value: form.textPrimary },
                  { label: t('admin.colors.border', 'Border'), value: form.borderColor },
                ].map((item) => (
                  <div key={item.label} style={{ textAlign: 'center' }}>
                    <div title={item.value} style={{ width: 22, height: 22, borderRadius: 999, border: '1px solid var(--border)', background: item.value, marginBottom: 4 }} />
                    <div style={{ fontSize: 9, color: 'var(--text-secondary)' }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <p style={{ marginTop: 10, color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('admin.appliesTenant', 'After saving, changes apply for all users in this tenant.')}
            </p>
          </div>}
        </div>
        </div>
      </div>
    </div>
  );
}
